import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { redirectToSsoLogin } from '../lib/api.js';

// All authentication happens at the CXFOP identity platform — the app never
// renders a password or MFA form (claims contract). This page only starts
// the OIDC redirect. While the platform SSO session (14 days) is valid the
// round-trip completes silently, so an expired app session re-auths without
// a visible login.
const ERROR_MESSAGES = {
  access_denied: 'Sign-in was cancelled or refused by the identity platform.',
  account_disabled: 'Your IPT account is deactivated. Contact the FOP IPT administrator.',
  link_ambiguous:
    'Your central account matches more than one IPT account. Contact the FOP IPT administrator to merge them.',
  email_conflict:
    'Your email address is already linked to a different IPT account. Contact the FOP IPT administrator.',
  login_expired: 'The sign-in attempt took too long. Please try again.',
  state_mismatch: 'The sign-in attempt could not be verified. Please try again.',
  sso_unavailable: 'Single sign-on is not configured on this server. Contact the administrator.',
  sso_error: 'Sign-in failed at the identity platform. Please try again.',
};

// Sticky signed-out marker — set by AuthContext.logout(), cleared only when
// the user explicitly clicks "Sign in with CXFOP". Survives the render race
// where a competing redirect drops the ?signedout=1 query param.
function signedOutFlag() {
  try {
    return sessionStorage.getItem('iptbc_signed_out') === '1';
  } catch {
    return false;
  }
}
function clearSignedOutFlag() {
  try {
    sessionStorage.removeItem('iptbc_signed_out');
  } catch {
    /* nothing to clear */
  }
}

export default function Login() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const error = params.get('error');
  const signedOut = params.get('signedout') === '1' || signedOutFlag();

  // Auto-start the redirect unless we have something to tell the user first
  // (an error, or a deliberate sign-out — silently re-authing right after
  // "Sign out" would make the button meaningless).
  useEffect(() => {
    if (!error && !signedOut && !user) redirectToSsoLogin({ returnTo: '/' });
  }, [error, signedOut, user]);

  // A still-authenticated visitor with nothing to read here goes home. The
  // signed-out and error states render even if stale user state lingers for
  // a frame — that stale bounce was exactly what made sign-out a no-op.
  if (user && !error && !signedOut) {
    return <Navigate to={user.role === 'none' ? '/pending' : '/'} replace />;
  }

  const signIn = () => {
    clearSignedOutFlag();
    // After an explicit sign-out, force an interactive re-auth at the
    // platform (prompt=login): someone at the keyboard cannot re-enter on
    // the silent SSO session alone. Error retries and the session-expiry
    // auto-redirect stay silent-capable — sign-out is the deliberate act.
    redirectToSsoLogin({ returnTo: '/', stepUp: signedOut });
  };

  return (
    <Centered>
      <div className="space-y-4">
        <h1 className="text-xl font-semibold text-cathay-700">Sign in</h1>
        <p className="text-sm text-slate-500">IPT Management App — FOP admin console</p>
        {error && (
          <div className="text-sm text-red-600">
            {ERROR_MESSAGES[error] || 'Sign-in failed. Please try again.'}
          </div>
        )}
        {signedOut && !error && (
          <div className="text-sm text-slate-600">
            You have been signed out of this app. Signing back in will ask for your CXFOP
            password.
          </div>
        )}
        {!error && !signedOut ? (
          <p className="text-sm text-slate-600">Redirecting to CXFOP sign-in…</p>
        ) : null}
        <button
          onClick={signIn}
          className="w-full bg-cathay-500 hover:bg-cathay-600 text-white py-2 rounded"
        >
          Sign in with CXFOP
        </button>
      </div>
    </Centered>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-sm bg-white rounded-lg shadow-md p-6">{children}</div>
    </div>
  );
}
